import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Container,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Link,
} from '@mui/material';
import {
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Link as RouterLink,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { ResetPasswordFormData } from '../../types/auth';
import { resetPasswordSchema } from '../../utils/validation';
import { useAuth } from '../../hooks/useAuth';
import PasswordStrengthMeter from '../../components/Auth/PasswordStrengthMeter';

const ResetPasswordPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);

  const { confirmPasswordReset, error: authError, clearError } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const currentYear = new Date().getFullYear();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<ResetPasswordFormData>({
    resolver: yupResolver(resetPasswordSchema) as any,
    mode: 'onChange',
    defaultValues: {
      password: '',
      confirmPassword: '',
      token: token || '',
    },
  });

  const watchedPassword = watch('password');

  useEffect(() => {
    if (!token) {
      setTokenError(
        '無効なリセットトークンです。パスワードリセットを再度お試しください。'
      );
    } else {
      setValue('token', token);
    }
  }, [token, setValue]);

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (isSubmitting || !token) return;

    setIsSubmitting(true);
    clearError();

    try {
      await confirmPasswordReset(data.token, data.password);
      setIsSubmitted(true);
    } catch (error) {
      console.error('Reset password error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const handleBackToLogin = () => {
    navigate('/auth/login');
  };

  const handleGoToForgotPassword = () => {
    navigate('/auth/forgot-password');
  };

  // Show success page after successful reset
  if (isSubmitted) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Container
          maxWidth={false}
          sx={{
            maxWidth: { xs: '100%', sm: '480px', md: '520px', lg: '560px' },
            width: '100%',
          }}
        >
          <Card
            elevation={8}
            sx={{
              borderRadius: { xs: 2, sm: 3, md: 4 },
              overflow: 'hidden',
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              width: '100%',
              maxWidth: '100%',
            }}
          >
            <CardContent
              sx={{ p: { xs: 3, sm: 4, md: 5, lg: 6 }, textAlign: 'center' }}
            >
              {/* Success Icon */}
              <CheckCircleIcon
                sx={{
                  fontSize: 64,
                  color: 'success.main',
                  mb: 2,
                }}
              />

              {/* Header */}
              <Typography variant="h5" component="h1" gutterBottom>
                パスワードが正常に変更されました
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                新しいパスワードでログインできます
              </Typography>

              {/* Login Button */}
              <Button
                variant="contained"
                size="large"
                onClick={handleBackToLogin}
                sx={{ py: 1.5, px: 4 }}
              >
                ログインページへ
              </Button>
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  // Show error page for invalid token
  if (tokenError) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Container
          maxWidth={false}
          sx={{
            maxWidth: { xs: '100%', sm: '480px', md: '520px', lg: '560px' },
            width: '100%',
          }}
        >
          <Card
            elevation={8}
            sx={{
              borderRadius: { xs: 2, sm: 3, md: 4 },
              overflow: 'hidden',
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              width: '100%',
              maxWidth: '100%',
            }}
          >
            <CardContent
              sx={{ p: { xs: 3, sm: 4, md: 5, lg: 6 }, textAlign: 'center' }}
            >
              {/* Error Alert */}
              <Alert severity="error" sx={{ mb: 3 }}>
                {tokenError}
              </Alert>

              {/* Header */}
              <Typography variant="h5" component="h1" gutterBottom>
                リンクが無効です
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                パスワードリセットのリンクが無効または期限切れです。
                新しいリセットリンクを取得してください。
              </Typography>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleGoToForgotPassword}
                  sx={{ py: 1.5 }}
                >
                  新しいリセットリンクを取得
                </Button>

                <Button
                  variant="outlined"
                  onClick={handleBackToLogin}
                  startIcon={<ArrowBackIcon />}
                  sx={{ py: 1.5 }}
                >
                  ログインページに戻る
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: { xs: 2, sm: 3, md: 4 },
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          maxWidth: { xs: '100%', sm: '480px', md: '520px', lg: '560px' },
          width: '100%',
        }}
      >
        <Card
          elevation={8}
          sx={{
            borderRadius: { xs: 2, sm: 3, md: 4 },
            overflow: 'hidden',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            width: '100%',
            maxWidth: '100%',
          }}
        >
          <CardContent sx={{ p: { xs: 3, sm: 4, md: 5, lg: 6 } }}>
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography
                variant="h4"
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: 'bold',
                  background: 'linear-gradient(45deg, #667eea, #764ba2)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                }}
              >
                新しいパスワードを設定
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                安全な新しいパスワードを入力してください
              </Typography>
            </Box>

            {/* Form */}
            <Box component="form" onSubmit={handleSubmit(onSubmit as any)}>
              {/* Error Alert */}
              {authError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {authError}
                </Alert>
              )}

              {/* Password Field */}
              <TextField
                {...register('password')}
                fullWidth
                label="新しいパスワード"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                margin="normal"
                error={!!errors.password}
                helperText={errors.password?.message}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 1 }}
              />

              {/* Password Strength Meter */}
              <PasswordStrengthMeter
                password={watchedPassword || ''}
                showFeedback={true}
              />

              {/* Confirm Password Field */}
              <TextField
                {...register('confirmPassword')}
                fullWidth
                label="パスワード確認"
                type={showConfirmPassword ? 'text' : 'password'}
                autoComplete="new-password"
                margin="normal"
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword?.message}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle confirm password visibility"
                        onClick={handleClickShowConfirmPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 3, mt: 3 }}
              />

              {/* Submit Button */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isSubmitting}
                sx={{
                  mb: 2,
                  py: 1.5,
                  position: 'relative',
                }}
              >
                {isSubmitting ? (
                  <>
                    <CircularProgress
                      size={20}
                      sx={{
                        position: 'absolute',
                        left: '50%',
                        marginLeft: '-10px',
                      }}
                    />
                    パスワード変更中...
                  </>
                ) : (
                  'パスワードを変更'
                )}
              </Button>

              {/* Back to Login Link */}
              <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Link
                  component={RouterLink}
                  to="/auth/login"
                  sx={{
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <ArrowBackIcon fontSize="small" />
                  ログインページに戻る
                </Link>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: { xs: 2, sm: 3, md: 4 } }}>
          <Typography
            variant="body2"
            color="white"
            sx={{
              opacity: 0.8,
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
            }}
          >
            © {currentYear} ガクフドキャリア. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default ResetPasswordPage;
